import React, { useState } from "react"
import {
	TLanguageValues,
	TOnClick,
} from "components/ProjectSpecificComponents/Interfaces/GlobalInterface"
import useScreenType from "components/ProjectNonSpecificComponents/Hooks/useScreenType"
import IconButton from "components/ProjectSpecificComponents/Elements/Global/atoms/Buttons/IconButton"
import HyperDiv from "components/ProjectNonSpecificComponents/Elements/HyperDiv/HyperDiv"
import PlainInput from "components/ProjectNonSpecificComponents/Elements/PlainInput"
import styled from "styled-components"
import Icon from "components/ProjectSpecificComponents/Elements/Global/atoms/Icons/Icon/Icon"
import { useTranslation } from "react-i18next"
import {
	IColor,
	IToolTipText,
} from "components/ProjectSpecificComponents/Interfaces/FormsInterface"
import { useFormStore } from "components/ProjectSpecificComponents/Contexts/FormStoreContext"
import ModalWithSepLine from "../../Global/molecules/Modals/ModalWithSepLine"
import Whitespace from "components/ProjectNonSpecificComponents/Elements/Whitespace"
import TooltipEditor from "./TooltipEditor"
import { observer } from "mobx-react"
import { type } from "os"

export interface IToolFieldPropertiesHeaderProps {
	label: string
	selectedLanguage: TLanguageValues
	titleInputValue?: string
	titleInputPlaceholder?: string
	tooltip?: IToolTipText
	tooltipPlaceholder?: string
	tooltipInputHidden?: boolean
	buttonColors?: IColor
	/**
	 * isFormHidden is used for mobile view to choose which icon to render based on if form is hidden or not
	 */
	isFormHidden?: boolean
	onChangeFormData?: (data: Partial<IToolFieldPropertiesHeaderFormData>) => void
	onClickLabel?: TOnClick
	onClickMoveUp?: TOnClick<HTMLButtonElement>
	onClickMoveDown?: TOnClick<HTMLButtonElement>
	onClickRemove?: TOnClick<HTMLButtonElement>
}

const ToolFieldPropertiesHeader = observer(
	({
		label,
		selectedLanguage,
		titleInputValue,
		titleInputPlaceholder,
		tooltip,
		tooltipPlaceholder,
		tooltipInputHidden = false,
		buttonColors,
		isFormHidden: isContentHidden,
		onChangeFormData = () => {},
		onClickLabel,
		onClickMoveUp,
		onClickMoveDown,
		onClickRemove,
	}: IToolFieldPropertiesHeaderProps) => {
		const formStore = useFormStore()
		const { isMobile } = useScreenType()
		const { t } = useTranslation()
		const [showTooltipModal, setShowTooltipModal] = useState<boolean>(false)
		const iconStyle: React.CSSProperties = {
			width: 32,
		}
		const tooltipKey = `text${selectedLanguage?.toUpperCase()}` as TToolFieldPropertiesTooltipText
		return (
			<HyperDiv
				style={{
					height: 36,
					backgroundColor: "var(--light-light-gray)",
					borderRadius: isMobile ? undefined : 5,
					overflow: "hidden",
				}}
				direction="row"
				spacedBetween
			>
				<HyperDiv direction="row">
					<TitleSection
						className="non-draggable"
						onClick={onClickLabel}
						style={{
							width: isMobile ? 62 : 124,
							backgroundColor: formStore.settingsData.personalize.colorButton,
						}}
					>
						<HyperDiv
							className="fill-cter"
							centered
							style={{
								fontWeight: label ? 600 : 100,
								fontStyle: label ? "normal" : "italic",
								opacity: label ? 1 : 0.4,
								color: buttonColors?.colorButtonText ?? "var(--white)",
							}}
						>
							<HyperDiv hidesOverflownText>
								{isMobile ? (
									<Icon
										iconColor={buttonColors?.colorButtonText ?? "var(--white)"}
										height={12}
										iconKey={isContentHidden ? "add" : "minus"}
									/>
								) : label ? (
									label
								) : (
									t("rawText.unspecified")
								)}
							</HyperDiv>
						</HyperDiv>
					</TitleSection>
					<HyperDiv direction="row" flex={1}>
						<SlashSection style={{ color: "var(--muddy-puddle-gray)" }} children="/" />
						<PlainInput
							style={{
								fontSize: 14,
								width: isMobile ? "100%" : undefined,
							}}
							value={titleInputValue}
							placeholder={titleInputPlaceholder}
							onChange={({ target: { value } }) => {
								onChangeFormData({ nameText: value })
							}}
						/>
					</HyperDiv>
					{!tooltipInputHidden && (
						<HyperDiv centeredY flex={1}>
							<SlashSection style={{ color: "var(--gray-c2)" }} children="/" />
							<div
								className="cursor-pointer"
								onClick={() => setShowTooltipModal(true)}
								style={{
									color: formStore.settingsData.personalize.colorButton,
								}}
							>
								{tooltipPlaceholder}
							</div>
						</HyperDiv>
					)}
				</HyperDiv>

				<HyperDiv direction="row">
					<IconButton
						style={iconStyle}
						iconHeight={12}
						iconColor={buttonColors?.colorButton}
						iconKey="arrow-up"
						disableUXPadding
						onClick={onClickMoveUp}
					/>
					<IconButton
						style={iconStyle}
						iconHeight={12}
						iconColor={buttonColors?.colorButton}
						iconKey="arrow-down"
						disableUXPadding
						onClick={onClickMoveDown}
					/>
					<IconButton
						style={iconStyle}
						iconHeight={12}
						iconColor={buttonColors?.colorButton}
						iconKey="cross"
						disableUXPadding
						onClick={onClickRemove}
					/>
				</HyperDiv>
				{showTooltipModal && formStore.selectedFormsConfig && tooltip && (
					<ModalWithSepLine title={t("rawText.tooltip")} onClose={() => setShowTooltipModal(false)}>
						<Whitespace x="1" />
						<TooltipEditor
							tooltipText={tooltip[tooltipKey]}
							tooltipIsRichText={tooltip?.isRichText}
							onCheckboxChange={(checked) => {
								onChangeFormData({ tooltip: { ...tooltip, isRichText: checked } })
							}}
							onInputChange={(text) => {
								onChangeFormData({ tooltip: { ...tooltip, [tooltipKey]: text } })
							}}
						/>
						<Whitespace x="1" />
					</ModalWithSepLine>
				)}
			</HyperDiv>
		)
	}
)

export default ToolFieldPropertiesHeader

export interface IToolFieldPropertiesHeaderFormData {
	nameText: string
	tooltip: IToolTipText
}
export type TToolFieldPropertiesTooltipText = "textLV" | "textEN"

const TitleSection = styled.div`
	height: 36px;
	padding: 0 0.5rem;
	cursor: pointer;

	display: flex;
	align-items: center;
	flex-shrink: 0;

	// Text
	font-size: 16px;
	font-weight: bold;
`

const SlashSection = styled.div`
	padding-left: 12px;
	padding-right: 8px;

	display: flex;
	align-items: center;
	flex-shrink: 0;
`
